import {Typography} from "@mui/material";
import React from "react";
import styled from "styled-components";
import ProfileIcon from "assets/profile_icon.png";
import {useMediaQuery} from "react-responsive";
import {HomeRepairService, House, Mail, QuestionMark, School} from "@mui/icons-material";
import {mailtoHref, SITE} from "site";

export default function AboutMe() {
    const isTabletOrMobile = useMediaQuery({query: "(max-width: 1224px)"});
    const isShort = useMediaQuery({query: "(max-height: 740px)"});
    const hideMark = useMediaQuery({query: "(max-height: 960px)"});
    const align = "left";
    const showIcon = !isTabletOrMobile && !hideMark;

    return (
        <Root $compact={isTabletOrMobile || isShort}>
            <Bio $stack={isTabletOrMobile}>
                <div style={{textAlign: align}}>
                    <Typography
                        component="h1"
                        style={{fontSize: isTabletOrMobile ? "24px" : "28px", lineHeight: 1.15}}
                    >
                        {SITE.name}
                    </Typography>
                    <Typography style={{fontSize: isTabletOrMobile ? "15px" : "16px"}}>
                        {SITE.headline}
                    </Typography>
                    <Typography style={{fontSize: "15px", color: "#e0e0e0", marginTop: "2px"}}>
                        {SITE.focus}
                    </Typography>
                    <Typography style={{fontSize: "15px", marginTop: "6px"}}>
                        {SITE.availability}
                    </Typography>
                    <Ctas>
                        <Cta href={mailtoHref}>Email</Cta>
                        <Cta
                            href={SITE.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            LinkedIn
                        </Cta>
                        <Cta
                            href={SITE.github}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            GitHub
                        </Cta>
                    </Ctas>
                </div>

                {showIcon && (
                    <div>
                        <img
                            style={{padding: "8px"}}
                            width={isTabletOrMobile ? "120px" : "160px"}
                            src={ProfileIcon}
                            alt=""
                            aria-hidden="true"
                        />
                    </div>
                )}
            </Bio>

            <About>
                <SectionLabel
                    align={align}
                    title="About"
                    icon={<QuestionMark fontSize="small" aria-hidden="true" />}
                />
                <Typography style={{textAlign: align, fontSize: "15px"}}>
                    Senior full-stack engineer on eCommerce and platform systems (C#, React,
                    Azure). I also run Splitrock Consulting for agentic and iOS work, and I
                    coach programming for FIRST Robotics team #95.
                </Typography>
            </About>

            <Facts $stack={isTabletOrMobile}>
                <About>
                    <SectionLabel
                        align={align}
                        title="Education"
                        icon={<School fontSize="small" aria-hidden="true" />}
                    />
                    <Typography style={{textAlign: align, fontSize: "15px"}}>
                        <b>B.S. Computer Science</b>
                        <br />
                        Rensselaer Polytechnic Institute
                        <br />
                        2018 - 2021
                    </Typography>
                </About>

                <About>
                    <SectionLabel
                        align={align}
                        title="Location"
                        icon={<House fontSize="small" aria-hidden="true" />}
                    />
                    <Typography style={{textAlign: align, fontSize: "15px"}}>
                        <b>Greater Boston Area, NH</b>
                    </Typography>
                </About>

                <About>
                    <SectionLabel
                        align={align}
                        title="Contact"
                        icon={<Mail fontSize="small" aria-hidden="true" />}
                    />
                    <Typography style={{textAlign: align, fontSize: "15px"}}>
                        <a href={mailtoHref}>
                            {SITE.email}
                        </a>
                        <br />
                        <a href={SITE.phoneHref}>
                            {SITE.phoneDisplay}
                        </a>
                    </Typography>
                </About>
            </Facts>

            <About>
                <SectionLabel
                    align={align}
                    title="Skills"
                    icon={<HomeRepairService fontSize="small" aria-hidden="true" />}
                />
                {isTabletOrMobile || isShort ? (
                    <Chips>
                        {SITE.skills.map((skill) => (
                            <Chip key={skill}>{skill}</Chip>
                        ))}
                    </Chips>
                ) : (
                    <SkillGroups>
                        {SITE.skillGroups.map((group) => (
                            <SkillGroup key={group.label} $center={false}>
                                <SkillGroupLabel>{group.label}</SkillGroupLabel>
                                <Chips>
                                    {group.items.map((skill) => (
                                        <Chip key={skill}>{skill}</Chip>
                                    ))}
                                </Chips>
                            </SkillGroup>
                        ))}
                    </SkillGroups>
                )}
            </About>
        </Root>
    );
}

function SectionLabel({
    align,
    title,
    icon,
}: {
    align: string;
    title: string;
    icon: React.ReactNode;
}) {
    return (
        <Typography
            component="h2"
            style={{
                textAlign: align as "left" | "center",
                fontSize: "16px",
                marginBottom: "0.25em",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                justifyContent: align === "center" ? "center" : "flex-start",
            }}
        >
            <u>{title}</u>
            {icon}
        </Typography>
    );
}

const Root = styled.div<{ $compact: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${(props) => (props.$compact ? "10px" : "12px")};
  min-width: 0;
`;

const About = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  text-align: start;
  line-height: 1.4;
  min-width: 0;
  max-width: 100%;
`;

const Facts = styled.div<{ $stack: boolean }>`
  display: grid;
  grid-template-columns: ${(props) => (props.$stack ? "1fr" : "repeat(3, minmax(0, 1fr))")};
  gap: 12px 16px;
`;

const Bio = styled.div<{ $stack: boolean }>`
  display: flex;
  align-items: flex-start;
  justify-content: ${(props) => (props.$stack ? "flex-start" : "space-between")};
  flex-direction: ${(props) => (props.$stack ? "column" : "row")};
  min-width: 0;
  width: 100%;

  > div {
    min-width: 0;
    max-width: 100%;
  }
`;

const Ctas = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
  justify-content: flex-start;
`;

const Cta = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 88px;
  padding: 6px 12px;
  border: 2px solid #8a8a8a;
  color: inherit;
  text-decoration: none;
  font-size: 15px;

  &:focus-visible {
    outline: 2px solid #f0f0f0;
    outline-offset: 3px;
  }
`;

const SkillGroups = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const SkillGroup = styled.div<{ $center: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: ${(props) => (props.$center ? "center" : "flex-start")};
  min-width: 0;
  max-width: 100%;
`;

const SkillGroupLabel = styled.span`
  font-size: 12px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #d0d0d0;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-width: 100%;
  justify-content: flex-start;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  border: 1px solid #8a8a8a;
  padding: 2px 8px;
  font-size: 13px;
  line-height: 1.3;
`;
